import { Box, Button, Card, CardContent, Divider, Stack, Typography } from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import { useTranslation } from "react-i18next";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { OrganizationContext } from "../../contexts/OrganizationContext";
import { useAuth } from "../../contexts/UserContext";
import { InputReactHookForm } from "../../components/input/InputReactHookForm";
import { CustomAutoComplete } from "../../components/input/CustomAutoComplete";
import { useForm } from "react-hook-form";
import OrganizationService from "../../service/organization.service";
import alertService from "../../components/alert/alertService";
import { common, input, organization as organizationI18n } from "../../i18n";
import { AvatarWithUpload } from "../../components/input/AvatarWithUpload";
import {
  ClassificationSourceValue,
  LISTING_STATUS_LABEL_KEY,
  ListingStatus,
  Organization,
  SectorRef,
  sectorRefLabel,
} from "../../models/Organization";
import CopyableField from "../../components/input/CopyableField";
import { INDUSTRY_TYPE } from "../../constants";
import { ONBOARDING_PATH } from "../../config/navigation";

const SOURCE_LINE: Record<ClassificationSourceValue, { Icon: typeof PersonOutlineIcon; key: string }> = {
  bank: { Icon: AccountBalanceOutlinedIcon, key: "Classification-Source-Bank" },
  borrower: { Icon: PersonOutlineIcon, key: "Classification-Source-Borrower" },
};

type OrganizationProfileFormValues = {
  organizationName: string;
  organizationNameEn: string;
  organizationNameTh: string;
  organizationType: string;
};

const OrganizationProfile = () => {
  const { t, i18n } = useTranslation([common, organizationI18n, input], {
    nsMode: "fallback",
  });
  const context = useContext(OrganizationContext);

  const { organization, selectOrganization, isAdmin, user, updateUserData } =
    useAuth();

  if (!context) {
    throw Error();
  }

  const [orgProfile, setOrgProfile] = useState<string | undefined>(undefined);
  const navigate = useNavigate();
  const location = useLocation();

  // a filled tile stretches gracefully where bare label+value text just strands itself
  const classificationTileSx = {
    bgcolor: "var(--gray3)",
    borderRadius: "8px",
    p: "16px 20px",
    height: "100%",
  };

  const sectorLabel = (sector?: SectorRef | null) => (sector ? sectorRefLabel(sector, i18n.language) : null);

  const listingStatusLabel = (status?: ListingStatus | null) => (status ? t(LISTING_STATUS_LABEL_KEY[status]) : null);

  // Only the admin can answer the wizard, and only while the server still asks.
  const canComplete = isAdmin() && context.onboardingStatus === "pending";
  const completeNow = () =>
    navigate(ONBOARDING_PATH, { state: { from: `${location.pathname}${location.search}${location.hash}` } });

  const classificationTile = (label: string, value: string | null, source?: ClassificationSourceValue | null) => {
    const line = source ? SOURCE_LINE[source] : null;
    return (
      <Stack gap={"8px"} sx={classificationTileSx} role="group" aria-label={label}>
        <Typography variant="h3">{label}</Typography>
        <Typography variant="text1">{value ?? t("Classification-Card-Empty")}</Typography>
        {value === null && canComplete && (
          <Button
            variant="text"
            color="primary"
            size="small"
            // inside the profile form, so it must never fall through to the page's submit
            type="button"
            onClick={completeNow}
            endIcon={<ArrowForwardIcon />}
            sx={{ alignSelf: "flex-start", px: 0 }}
          >
            {t("Onboarding-Reminder-Action")}
          </Button>
        )}
        {line && (
          <Box display="flex" alignItems="center" gap={0.75}>
            <line.Icon sx={{ fontSize: 16, color: "var(--gray9)" }} />
            <Typography variant="body2" color="var(--gray9)">
              {t(line.key)}
            </Typography>
          </Box>
        )}
      </Stack>
    );
  };

  const { control, setValue, handleSubmit } =
    useForm<OrganizationProfileFormValues>({
      mode: "all",
      reValidateMode: "onChange",
      defaultValues: {
        organizationName: "",
        organizationNameEn: "",
        organizationNameTh: "",
        organizationType: "",
      },
    });

  useEffect(() => {
    if (context && context.organization) {
      setValue("organizationName", context.organization.name ?? "");
      setValue("organizationNameEn", context.organization.nameEn ?? "");
      setValue("organizationNameTh", context.organization.nameTh ?? "");
      const businessTypeEn = context.organization.businessTypeEn ?? "";
      const isInList = INDUSTRY_TYPE.some((item) => item.value === businessTypeEn);
      setValue("organizationType", isInList ? businessTypeEn : "");
      setOrgProfile(context.organization.orgProfile ?? undefined);
    }
  }, [context.organization]);

  const onSubmit = async (values: OrganizationProfileFormValues) => {
    const selectedItem = INDUSTRY_TYPE.find(
      (item) => item.value === values.organizationType,
    );
    const businessTypeTh = selectedItem?.nameTH ?? values.organizationType;

    let result: Organization | null = null;
    try {
      result = await OrganizationService.updateOrganizationsV2(
        values.organizationName,
        values.organizationNameEn,
        values.organizationNameTh,
        organization?.taxId ?? "",
        values.organizationType,
        businessTypeTh,
        orgProfile ?? "",
      );
    } catch {
      // error alert already shown by service
    }

    if (result) {
      alertService.mixin(t("edit-successfully"));

      const temp = user;

      const index =
        temp?.organizationsRef?.findIndex((o) => o.taxId === result!.taxId) ??
        -1;

      if (index >= 0 && temp) {
        temp!.organizationsRef![index] = {
          ...result,
          id: temp!.organizationsRef![index].id,
        };
        updateUserData(temp);
      }

      selectOrganization({
        id: organization?.id ?? 0,
        taxId: organization?.taxId ?? "",
        name: values.organizationName,
        businessType: values.organizationType ?? "",
        orgProfile: orgProfile,
      });

      context.setOrganization(result);
    }
  };

  return (
    <>
      <form
        onSubmit={handleSubmit(onSubmit)}
        style={{
          minWidth: "100%",
        }}
      >
        <Box
          sx={{
            height: "100%",
            width: "100%",
          }}
          display={"flex"}
          flexDirection={"column"}
          gap={"40px"}
        >
          <Box
            flex={1}
            sx={{
              overflow: "auto",
              height: "100%",
              width: "100%",
              scrollbarGutter: "stable",
            }}
          >
            <Grid2
              container
              rowSpacing={"20px"}
              columnSpacing={{ xs: "0px", md: "40px" }}
              sx={{
                width: "100%",
              }}
            >
              <Grid2 direction={"row"} gap={"20px"} xs={12} display={"flex"}>
                <AvatarWithUpload
                  avatar={orgProfile}
                  displayName={organization?.name ?? ""}
                  setAvatarChange={setOrgProfile}
                  hidden={!isAdmin()}
                />

                <Stack gap={"8px"} justifyContent={"center"}>
                  <Typography variant="h3">{t("Organization-Logo")}</Typography>
                  <Typography variant="text1" color={"var(--gray4)"}>
                    {t("Organization-Logo-Description")}
                  </Typography>
                </Stack>
              </Grid2>
              <Grid2 xs={12} md={6}>
                <InputReactHookForm
                  name="organizationName"
                  label={t("Organization-Display-Name")}
                  info={t("Organization-Display-Name")}
                  placeholder={t("Organization-Display-Name")}
                  control={control}
                  required
                  rules={{
                    required: t("This-field-is-required", {
                      param: t("Organization-Display-Name"),
                    }),
                    pattern: {
                      value: /^[^,]*$/,
                      message: t("Name-Fails-To-Match-Pattern"),
                    },
                  }}
                  defaultValue={""}
                  disabled={!isAdmin()}
                />
              </Grid2>
              <Grid2 xs={12} md={6}>
                <Stack gap={"8px"}>
                  <Typography variant="h3">{t("Organization-ID")}</Typography>

                  <CopyableField value={`${organization?.id}`} />
                </Stack>
              </Grid2>

              <Grid2 xs={12} md={6}>
                <InputReactHookForm
                  name="organizationNameEn"
                  label={t("Organization-Name-English")}
                  info={t("Organization-Name-English")}
                  placeholder={t("Organization-Name-English")}
                  control={control}
                  required
                  rules={{
                    required: t("This-field-is-required", {
                      param: t("Organization-Name-English"),
                    }),
                    pattern: {
                      value: /^(?=.*\S)[A-Za-z0-9\s\-_.;\/()]+$/,
                      message: t("English-Only-Allowed"),
                    },
                  }}
                  defaultValue={""}
                  disabled={!isAdmin()}
                />
              </Grid2>

              <Grid2 xs={12} md={6}>
                <InputReactHookForm
                  name="organizationNameTh"
                  label={t("Organization-Name-Thai")}
                  info={t("Organization-Name-Thai")}
                  placeholder={t("Organization-Name-Thai")}
                  control={control}
                  required
                  rules={{
                    required: t("This-field-is-required", {
                      param: t("Organization-Name-Thai"),
                    }),
                    pattern: {
                      value: /^(?=.*\S)[ก-๙0-9\s\-_.;\/()]+$/,
                      message: t("Thai-Only-Allowed"),
                    },
                  }}
                  defaultValue={""}
                  disabled={!isAdmin()}
                />
              </Grid2>

              <Grid2 xs={12} md={6}>
                <CustomAutoComplete
                  name="organizationType"
                  label={t("Industry-Type")}
                  info={t("Industry-Type")}
                  control={control}
                  defaultValue={""}
                  placeholder={t("Industry-Type")}
                  listMenuItem={INDUSTRY_TYPE}
                  disabled={!isAdmin()}
                />
              </Grid2>
            </Grid2>

          {/* Read-only and outside the form above: the bank owns these values, so nothing here is ever saved. */}
          {context.organization?.orgType === "borrower" && (
            <Card variant="outlined" sx={{ mt: "40px", borderRadius: "8px" }}>
              {/* longhand + the :last-child twin: MUI CardContent ships its own :last-child padding-bottom,
                  and the extra 24px is the owner's measured value from a live resize on the real page */}
              <CardContent
                sx={{
                  pt: "20px",
                  px: "32px",
                  pb: "56px",
                  "&:last-child": { pb: "56px" },
                }}
              >
              <Stack direction="row" alignItems="center" justifyContent="space-between" minHeight="40px" mb={2}>
                <Typography variant="h2">{t("Classification-Card-Title")}</Typography>
                <Typography variant="body2" color="var(--gray9)">
                  {t("Classification-Tell-Bank")}
                </Typography>
              </Stack>

              <Divider sx={{ mb: "24px" }} />

              {/* css grid, not Grid2: Grid2 spaces with negative margins, so CardContent measured ~20px
                  short and Card's overflow:hidden clipped the tiles flush against the bottom border */}
              <Box
                sx={{
                  display: "grid",
                  gap: "20px",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(3, 1fr)" },
                }}
              >
                {classificationTile(
                  t("Classification-Sector-Tsic-Label"),
                  sectorLabel(context.organization.sectorTsic),
                  context.organization.classificationSource?.sectorTsic
                )}
                {classificationTile(
                  t("Classification-Sector-Isic-Label"),
                  sectorLabel(context.organization.sectorIsic),
                  context.organization.classificationSource?.sectorIsic
                )}
                {classificationTile(
                  t("Classification-Card-Listing-Status"),
                  listingStatusLabel(context.organization.listingStatus),
                  context.organization.classificationSource?.listingStatus
                )}
              </Box>
              </CardContent>
            </Card>
          )}
          </Box>
          {isAdmin() ? (
            <Button
              variant="contained"
              color="secondary"
              disabled={!isAdmin()}
              type="submit"
            >
              {t("Save")}
            </Button>
          ) : null}
        </Box>
      </form>
    </>
  );
};

export default OrganizationProfile;
